export const PRODUCT_SORT_OPTIONS = [
  'recommended',
  'newest',
  'popular',
  'name_asc',
  'price_asc',
  'price_desc',
] as const;

export type ProductSortOption = (typeof PRODUCT_SORT_OPTIONS)[number];

export const DEFAULT_PRODUCT_SORT: ProductSortOption = 'recommended';

export const isProductSortOption = (value: unknown): value is ProductSortOption =>
  typeof value === 'string' && (PRODUCT_SORT_OPTIONS as readonly string[]).includes(value);

export interface ProductSortOptionDefinition {
  value: ProductSortOption;
  labelKey: string;
  defaultLabel: string;
}

export const PRODUCT_SORT_OPTION_DEFINITIONS: ProductSortOptionDefinition[] = [
  { value: 'recommended', labelKey: 'sort.recommended', defaultLabel: 'Recommended' },
  { value: 'newest', labelKey: 'sort.newest', defaultLabel: 'Newest' },
  { value: 'popular', labelKey: 'sort.mostPopular', defaultLabel: 'Most Popular' },
  { value: 'name_asc', labelKey: 'sort.nameAsc', defaultLabel: 'A to Z' },
  { value: 'price_asc', labelKey: 'sort.priceLowHigh', defaultLabel: 'Price: Low to High' },
  { value: 'price_desc', labelKey: 'sort.priceHighLow', defaultLabel: 'Price: High to Low' },
];

type FacetConfigurationLike = {
  key: string;
  type: string;
  selected?: boolean;
  min?: number;
  max?: number;
  minSelected?: number;
  maxSelected?: number;
  terms?: Array<{ key: string; selected?: boolean }>;
};

export const buildProductQueryParams = ({
  facetsConfiguration,
  searchQuery,
  categoryId,
  limit,
  sortBy,
}: {
  facetsConfiguration: Record<string, FacetConfigurationLike>;
  searchQuery?: string;
  categoryId?: string;
  limit?: number;
  sortBy: ProductSortOption;
}): Record<string, string | string[]> => {
  const params: Record<string, string | string[]> = { sortBy };

  if (limit !== undefined) params.limit = String(limit);

  if (searchQuery) params.q = searchQuery;
  if (categoryId) params['categories[]'] = categoryId;

  Object.values(facetsConfiguration).forEach((configuration) => {
    if (!configuration.selected) return;

    if (configuration.type === 'range') {
      params[`facets[${configuration.key}][min]`] = String(configuration.minSelected ?? configuration.min);
      params[`facets[${configuration.key}][max]`] = String(configuration.maxSelected ?? configuration.max);
    } else if (configuration.type === 'term' || configuration.type === 'color') {
      if (configuration.key === 'categories.categoryId') {
        params['categories[]'] = (configuration.terms ?? []).filter((term) => term.selected).map((term) => term.key);
      } else {
        (configuration.terms ?? [])
          .filter((term) => term.selected)
          .forEach((term, index) => {
            params[`facets[${configuration.key}][terms][${index}]`] = term.key;
          });
      }
    } else if (configuration.type === 'boolean') {
      const selectedTerm = (configuration.terms ?? []).find((term) => term.selected);
      if (selectedTerm) params[`facets[${configuration.key}][boolean]`] = selectedTerm.key;
    }
  });

  return params;
};
